/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   LakehouseBackupStatusCell.tsx
 *     Feature         :   286 - FCM licence enforcement and estate limits (T159 item 26)
 *     Description     :   The lakehouse list's Backup Status, on Spec 201's definitions: an ACTIVE plan targets it
 *                         ("Protected"), only paused plans do ("Plan paused"), or none ("No plan"). The
 *                         dbo.lakehouses.backup_enabled switch still stops the executor, so with it off the cell reads
 *                         "Backups off". Until item 26 it read the switch alone: "Enabled" on every lakehouse on dev
 *                         (2026-10-22) while 109 of PersonalMed's had only paused plans and 23 had none.
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import React from 'react';
import { CheckCircle } from 'lucide-react';
import { AppTooltip, Badge } from '@/components/ui';

export type LakehouseProtection = 'protected' | 'paused' | 'none';

export interface LakehouseBackupStatusCellProps {
  protection: LakehouseProtection;
  /** dbo.lakehouses.backup_enabled: off stops every schedule (scheduleRepository.findDueSchedules). */
  backupEnabled: boolean;
}

export const LakehouseBackupStatusCell: React.FC<LakehouseBackupStatusCellProps> = ({ protection, backupEnabled }) => {
  if (!backupEnabled) {
    return (
      <AppTooltip title="Backups are switched off for this lakehouse: no plan or schedule runs while the switch is off.">
        <Badge variant="secondary">Backups off</Badge>
      </AppTooltip>
    );
  }
  if (protection === 'protected') {
    return (
      <AppTooltip title="An active backup plan covers this lakehouse.">
        <Badge variant="success" leftIcon={<CheckCircle className="h-3.5 w-3.5" />}>Protected</Badge>
      </AppTooltip>
    );
  }
  if (protection === 'paused') {
    return (
      <AppTooltip title="The backup plans that cover this lakehouse are paused, so it is not being backed up.">
        <Badge variant="warning">Plan paused</Badge>
      </AppTooltip>
    );
  }
  return (
    <AppTooltip title="No backup plan covers this lakehouse.">
      <Badge variant="secondary">No plan</Badge>
    </AppTooltip>
  );
};

export default LakehouseBackupStatusCell;
